import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, InputGroup, ProgressBar } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar, X } from "react-feather";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import { getMRGroupsMstrlst, saveMRGroupsMstrInsert, saveReportDocument } from "../../apiServices/reducer/apiService";
import StethoscopeLoading from "../../components/StethoscopeLoading/StethoscopeLoading";
import { useDispatch, useSelector } from "react-redux";
import "../../styles/user.css";
import { showToast } from "../../Toaster";
import { showPmrSuccess } from "../../utils/swalUtils";
import CustomSuccessModal from "../../components/CustomSuccessModal";

const UploadReports = ({ show, onHide, onUploadSuccess, userId }) => {
  const dispatch = useDispatch();
  // State variables
  const [formValues, setFormValues] = useState({
    reportName: "",
    groupName: "",
    newGroupName: "",
    providerName: "",
    hospitalName: "",
    reportDate: null,
    allowMultiple: true
  });

  const [files, setFiles] = useState([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [groupsLst, setGroupsLst] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shouldUpload, setShouldUpload] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const fileInputRef = useRef(null);
  const uploadInProgressRef = useRef(false);
  //const userId = useSelector((state) => state.apiService?.login?.user_id);
  const token = useSelector((state) => state.apiService?.token);

  // Add this new state for field errors
  const [fieldErrors, setFieldErrors] = useState({
    reportName: false,
    groupName: false,
    files: false,
    reportDate: false,
    providerName: false,
    hospitalName: false
  });

  // Add state for drag and drop visual feedback
  const [isDragOver, setIsDragOver] = useState(false);

  // Reset form and file state
  const resetForm = () => {
    setFormValues({
      reportName: "",
      groupName: "",
      newGroupName: "",
      providerName: "",
      hospitalName: "",
      reportDate: null,
      allowMultiple: true
    });
    setFiles([]);
    setIsAddingNew(false);
    setSelectedGroup("");
    setSubmitted(false);
    setProgress(0);
    setError(null);
    setShouldUpload(false);
    setIsDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Handle modal close: reset form then call onHide
  const handleClose = () => {
    resetForm();
    // Reset field errors when closing modal
    setFieldErrors({
      reportName: false,
      groupName: false,
      files: false,
      reportDate: false,
      providerName: false,
      hospitalName: false
    });
    onHide();
    // Refresh the page when close button is clicked
    // window.location.reload();
  };

  // Handle upload effect
  useEffect(() => {
    if (shouldUpload && !uploadInProgressRef.current) {
      handleUpload();
    }
  }, [shouldUpload]);

  // Debug effect to monitor files state changes
  useEffect(() => {
  }, [files]);

  const handleUpload = async () => {
    if (uploadInProgressRef.current) return;

    // Only document selection is mandatory
    if (files.length === 0) {
      //showToast('error', 'Please select a document to upload.'); 
      return;
    }

    uploadInProgressRef.current = true;
    setSubmitted(true);

    try {
      let reportTypeName = formValues.reportName || 'Unknown Type';

      for (const file of files) {
        const formData = new FormData();
        formData.append('chunk', file);
        formData.append('Index', '0');
        formData.append('TotalChunks', '1');
        formData.append('FileName', file.name);
        // If no group is selected, upload as general (GroupID = '0')
        formData.append('GroupID', selectedGroup || '0');
        formData.append('TagNo', formValues.reportName);
        formData.append('ProviderName', formValues.providerName);
        formData.append('HospitalName', formValues.hospitalName);
        formData.append('UserID', userId);
        formData.append('SiteID', '0');
        formData.append('ReportDate', formValues.reportDate ? formatDateToYYYYMMDD(formValues.reportDate) : '');
        formData.append('token', token);

        setProgress(30);

        const resultAction = await dispatch(saveReportDocument(formData));

        if (saveReportDocument.fulfilled.match(resultAction)) {
          // Get report type name from selected group (store for later use)
          const selectedGroupData = groupsLst.find(group => group.id === selectedGroup);
          reportTypeName = selectedGroupData ? selectedGroupData.name : (formValues.reportName || 'Unknown Type');
        } else {
          throw new Error(resultAction.error?.message || 'Failed to upload report');
        }
      }

      // Show success modal only after all files are uploaded successfully
      const fileText = files.length > 1 ? 'reports have' : 'report has';
      showPmrSuccess({
        html: `<div class="pmr-popup-message">Your <span style="color: var(--pmr-text-heading); font-weight: bold;">${reportTypeName}</span> ${fileText} been uploaded successfully.</div>`,
        timer: 2000,
        customClass: {
          popup: 'swal2-animate-up',
        },
      });
      setProgress(100);
      setTimeout(() => {
        setProgress(0);
        setSubmitted(false);
        uploadInProgressRef.current = false;
        setShouldUpload(false);
        resetForm();
        onHide();
        onUploadSuccess();
      }, 2000);
    } catch (error) {
      showToast('error', error.message || 'Failed to upload report');
      setProgress(0);
      setSubmitted(false);
      uploadInProgressRef.current = false;
      setShouldUpload(false);
    }
  };

  const onUpload = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset previous errors
    setFieldErrors({
      reportName: false,
      groupName: false,
      files: false,
      reportDate: false,
      providerName: false,
      hospitalName: false
    });

    // Only Report Name is mandatory
    const newErrors = {
      reportName: !formValues.reportName.trim(),
      groupName: false,
      files: false,
      reportDate: false,
      providerName: false,
      hospitalName: false
    };

    setFieldErrors(newErrors);

    // If Report Name is missing, stop submission
    if (newErrors.reportName) {
      return;
    }

    if (!uploadInProgressRef.current) {
      setShouldUpload(true);
      handleUpload();
    }
  };

  // Load groups when component mounts
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!token || !userId) {
          throw new Error('Authentication token or user ID not found');
        }

        // Create the request body with required parameters
        const requestBody = {
          token: token,
          user_id: userId,
          SiteID: 0,
          Active: true
        };

        // Dispatch the thunk action with the request body
        const resultAction = await dispatch(getMRGroupsMstrlst(requestBody));

        if (getMRGroupsMstrlst.fulfilled.match(resultAction)) {
          const response = resultAction.payload;
          // Check if response exists
          if (!response) {
            setError('No response received from server');
            return;
          }

          // Handle different response structures
          let groupsData;
          if (response.Table && Array.isArray(response.Table)) {
            groupsData = response.Table;
          } else if (Array.isArray(response)) {
            groupsData = response;
          } else if (response.data) {
            groupsData = response.data.Table || response.data;
          } else {
            groupsData = response;
          }
          if (!groupsData || !Array.isArray(groupsData)) {
            setError('Invalid response format from server');
            return;
          }

          // Transform the response data into the required format
          const transformedGroups = groupsData
            .filter(group => group.name !== 'All') // Filter out the "All" option
            .map((group) => ({
              id: group.id?.toString() || group.GroupID?.toString() || '0',
              name: group.name || group.GroupName || 'Unnamed Group',
              is_deletable: group.is_deletable === 1
            }));
          if (transformedGroups.length > 0) {
            setGroupsLst(transformedGroups);
          } else {
            setError('No groups found. Please try again later.');
          }
        } else {
          setError('Failed to load groups. Please try again later.');
        }
      } catch (error) {
        setError('Failed to load groups. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [dispatch, token, userId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };

  const handleDateChange = (date) => {
    setFormValues({
      ...formValues,
      reportDate: date
    });
  };

  const uploadFiles = (e) => {
    try {
      const selectedFiles = Array.from(e.target.files);
      if (selectedFiles.length > 0) {
        let filesToAdd;

        if (formValues.allowMultiple) {
          // Add to existing files instead of replacing
          const existingFiles = [...files];
          const newFiles = [...selectedFiles];

          // Filter out duplicates based on file name and size
          const filteredNewFiles = newFiles.filter(newFile =>
            !existingFiles.some(existingFile =>
              existingFile.name === newFile.name && existingFile.size === newFile.size
            )
          );

          filesToAdd = [...existingFiles, ...filteredNewFiles];
        } else {
          // Single file mode - replace
          filesToAdd = [selectedFiles[0]];
        }
        setFiles(filesToAdd);

        // Auto-fill report name with appropriate name based on number of files
        let reportName = '';
        if (filesToAdd.length === 1) {
          reportName = filesToAdd[0].name.split('.').slice(0, -1).join('.');
        } else {
          reportName = `${filesToAdd.length} files selected`;
        }
        setFormValues(prev => ({ ...prev, reportName: reportName }));

        // Clear any file input errors
        setFieldErrors(prev => ({ ...prev, files: false }));
      }

      // Reset the input value to allow selecting the same files again if needed
      if (e.target) {
        e.target.value = '';
      }
    } catch (error) {
    }
  };

  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);

    // Update report name based on remaining files
    let reportName = '';
    if (newFiles.length === 0) {
      reportName = "";
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else if (newFiles.length === 1) {
      reportName = newFiles[0].name.split('.').slice(0, -1).join('.');
    } else {
      reportName = `${newFiles.length} files selected`;
    }

    setFormValues(prev => ({ ...prev, reportName: reportName }));

    // Clear file error if there are still files
    if (newFiles.length > 0) {
      setFieldErrors(prev => ({ ...prev, files: false }));
    }
  };

  const removeAllFiles = () => {
    setFiles([]);
    setFormValues(prev => ({ ...prev, reportName: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    // Clear any file input errors
    setFieldErrors(prev => ({ ...prev, files: false, reportName: false }));
  };

  const getFileNameWithoutExtension = (fileName) => {
    return fileName.split('.').slice(0, -1).join('.');
  };

  // Helper function to format date to YYYY-MM-DD without timezone issues
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toggleAddNew = () => {
    setIsAddingNew(!isAddingNew);
  };

  const onGroupChange = (value) => {
    setSelectedGroup(value);
    setFormValues({
      ...formValues,
      groupName: value
    });
  };

  const addNewGroup = async () => {
    if (!formValues.newGroupName || !formValues.newGroupName.trim()) {
      return;
    }

    // Prevent multiple clicks
    if (loading) return;

    // Check if the group name already exists
    const trimmedName = formValues.newGroupName.trim();
    const existingGroup = groupsLst.find(group =>
      group.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingGroup) {
      // Auto-select the existing group instead of showing error
      setSelectedGroup(existingGroup.id);
      setFormValues(prev => ({
        ...prev,
        groupName: existingGroup.name,
        newGroupName: ""
      }));
      setIsAddingNew(false);
      setSuccessMessage(`Report type "<span style="color: var(--pmr-primary); font-weight: bold;">${existingGroup.name}</span>" already exists and has been selected.`);
      setShowSuccessModal(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Call the API to save the new group
      const resultAction = await dispatch(saveMRGroupsMstrInsert({
        GroupName: trimmedName,
        UserID: userId,
        token: token
      }));
      if (saveMRGroupsMstrInsert.fulfilled.match(resultAction)) {
        // Check if the API returned an "already exists" message
        if (resultAction.payload && resultAction.payload.Return_Message === 'Report Name Already Exists.') {
          // Try to find the existing group in the current list or refresh the list
          let foundGroup = groupsLst.find(group =>
            group.name.toLowerCase() === trimmedName.toLowerCase()
          );

          if (!foundGroup) {
            // Group exists in database but not in current list, refresh the list 
            const refreshResult = await dispatch(getMRGroupsMstrlst({
              token: token,
              user_id: userId,
              SiteID: 0,
              Active: true
            }));

            if (getMRGroupsMstrlst.fulfilled.match(refreshResult)) {
              const refreshResponse = refreshResult.payload;
              if (refreshResponse && refreshResponse.Table && Array.isArray(refreshResponse.Table)) {
                const refreshGroupsData = refreshResponse.Table.filter(group => group.name !== 'All');
                const refreshTransformedGroups = refreshGroupsData.map((group) => ({
                  id: group.id?.toString() || group.GroupID?.toString() || '0',
                  name: group.name || group.GroupName || 'Unnamed Group',
                  is_deletable: group.is_deletable === 1
                }));

                setGroupsLst(refreshTransformedGroups);

                foundGroup = refreshTransformedGroups.find(group =>
                  group.name.toLowerCase() === trimmedName.toLowerCase()
                );
              }
            }
          }

          if (foundGroup) {
            setSelectedGroup(foundGroup.id);
            setFormValues(prev => ({
              ...prev,
              groupName: foundGroup.name,
              newGroupName: ""
            }));
            setSuccessMessage(`Report type "<span style="color: var(--pmr-primary); font-weight: bold;">${foundGroup.name}</span>" already exists and has been selected.`);
            setShowSuccessModal(true);
            setIsAddingNew(false);
            return;
          } else {
            // If group exists in database but not in current list, just show a friendly message
            setSuccessMessage(`Report type "${trimmedName}" already exists. Please try refreshing the page if you don't see it in the dropdown.`);
            setShowSuccessModal(true);
            setIsAddingNew(false);
            return;
          }
        }

        // Immediately fetch the updated list after successful save
        const fetchResult = await dispatch(getMRGroupsMstrlst({
          token: token,
          user_id: userId,
          SiteID: 0,
          Active: true
        }));

        if (getMRGroupsMstrlst.fulfilled.match(fetchResult)) {
          const response = fetchResult.payload;
          if (response && response.Table && Array.isArray(response.Table)) {
            const groupsData = response.Table.filter(group => group.name !== 'All');
            const transformedGroups = groupsData.map((group) => ({
              id: group.id?.toString() || group.GroupID?.toString() || '0',
              name: group.name || group.GroupName || 'Unnamed Group',
              is_deletable: group.is_deletable === 1
            }));

            // Find the newly added group by name (case-insensitive)
            const newlyAddedGroup = transformedGroups.find(group =>
              group.name.toLowerCase() === trimmedName.toLowerCase()
            );

            // Update states in a single batch to prevent jerking
            if (newlyAddedGroup) {
              setSelectedGroup(newlyAddedGroup.id);
              setFormValues(prev => ({
                ...prev,
                groupName: newlyAddedGroup.name,
                newGroupName: ""
              }));
              setGroupsLst(transformedGroups);
              setSuccessMessage(`Report type "<span style="color: var(--pmr-primary); font-weight: bold;">${newlyAddedGroup.name}</span>" added successfully!`);
              setShowSuccessModal(true);
              // Call onUploadSuccess to refresh only the reports page instead of reloading the entire application
              if (onUploadSuccess) {
                onUploadSuccess();
              }
            } else {
              // If newly added group not found, select the last group as fallback
              if (transformedGroups.length > 0) {
                const lastGroup = transformedGroups[transformedGroups.length - 1];
                setSelectedGroup(lastGroup.id);
                setFormValues(prev => ({
                  ...prev,
                  groupName: lastGroup.name,
                  newGroupName: ""
                }));
                setGroupsLst(transformedGroups);
                setSuccessMessage(`Report type "<span style="color: var(--pmr-primary); font-weight: bold;">${lastGroup.name}</span>" selected. New type may take a moment to appear.`);
                setShowSuccessModal(true);
              }
            }
          }
        }

        setIsAddingNew(false);
      } else {
        setError('Failed to save new report type. Please try again.');
      }
    } catch (error) {
      setError('Failed to save new report type. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cancelNewGroup = () => {
    setFormValues({
      ...formValues,
      newGroupName: ""
    });
    setIsAddingNew(false);
  };

  // Add CSS styles for error state
  const inputErrorStyle = {
    border: '1px solid red',
    boxShadow: 'none'
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static" keyboard={false} enforceFocus={false}>
      <Modal.Header className="custom-modal-header">
        <Modal.Title className="text-capitalize fw-bold">Upload Report</Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleClose}
        ></button>
      </Modal.Header>
      <Modal.Body style={{ padding: "2rem 2.5rem" }}>
        <div className="mb-4">
          <span style={{ color: "#ff9800", fontWeight: "bold", fontSize: 16 }}>
            Introducing a new feature! You can now upload reports to your Acme Health account by sending an email to <span style={{ color: "#ff9800" }}>support@acmehealth.ai</span> with your reports attached from your Acme Health-registered email address.
          </span>
        </div>
        <Form autoComplete="off">
          <Form.Group className="mb-3">
            <Form.Label className="fw-normal">
              Report Name: <span className="text-danger">*</span>
            </Form.Label>
            {/* <InputGroup>
              <Form.Control
                type="text"
                name="reportName"
                value={formValues.reportName}
                onChange={handleInputChange}
                className={`flatpickr-input-custom ${fieldErrors.reportName ? 'error-border' : ''}`}
                style={fieldErrors.reportName ? inputErrorStyle : {}}
              />
              <Button
                 className="choose-documents-btn text-capitalize"
                onClick={() => fileInputRef.current.click()}
              >
                Choose Document(s)
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                id="account-upload"
                hidden
                multiple={formValues.allowMultiple}
                onChange={uploadFiles}
              />
              {files.length > 0 && (
                <Button
                  variant="outline-danger"
                  onClick={removeAllFiles}
                  title="Remove All Files"
                  className="remove-all-files-btn ms-2"
                  style={{ height: '50px' }}
                >
                  <X size={22} color="var(--pmr-danger)" />
                </Button>
              )}
            </InputGroup>  */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(true);
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Only set to false if we're leaving the drop zone entirely
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setIsDragOver(false);
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(false);

                try {
                  const droppedFiles = Array.from(e.dataTransfer.files);

                  if (droppedFiles.length > 0) {
                    let filesToAdd;

                    if (formValues.allowMultiple) {
                      // Add to existing files instead of replacing
                      const existingFiles = [...files];
                      const newFiles = [...droppedFiles];

                      // Filter out duplicates based on file name and size
                      const filteredNewFiles = newFiles.filter(newFile =>
                        !existingFiles.some(existingFile =>
                          existingFile.name === newFile.name && existingFile.size === newFile.size
                        )
                      );

                      filesToAdd = [...existingFiles, ...filteredNewFiles];
                    } else {
                      // Single file mode - replace
                      filesToAdd = [droppedFiles[0]];
                    }

                    setFiles(filesToAdd);

                    // Auto-fill report name with appropriate name based on number of files
                    let reportName = '';
                    if (filesToAdd.length === 1) {
                      reportName = filesToAdd[0].name.split('.').slice(0, -1).join('.');
                    } else {
                      reportName = `${filesToAdd.length} files selected`;
                    }
                    setFormValues(prev => ({ ...prev, reportName: reportName }));

                    // Clear any file input errors
                    setFieldErrors(prev => ({ ...prev, files: false }));
                  } else {
                  }
                } catch (error) {
                }
              }}
              className={`drag-drop-area ${isDragOver ? 'drag-over' : ''}`}
              style={{ fontSize: '12px', marginTop: '5px', fontWeight: 'bold', width: '100%', marginBottom: '30px' }}
            >
              <InputGroup>
                <Form.Control
                  type="text"
                  name="reportName"
                  value={formValues.reportName}
                  onChange={handleInputChange}
                  className={`flatpickr-input-custom ${fieldErrors.reportName ? 'error-border' : ''}`}
                  style={fieldErrors.reportName ? inputErrorStyle : {}}
                />
                <Button
                  className="choose-documents-btn text-capitalize"
                  onClick={() => fileInputRef.current.click()}
                >
                  Choose Document(s)
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  id="account-upload"
                  hidden
                  multiple={formValues.allowMultiple}
                  onChange={uploadFiles}
                />
                {files.length > 0 && (
                  <Button
                    variant="outline-danger"
                    onClick={removeAllFiles}
                    title="Remove All Files"
                    className="remove-all-files-btn ms-2"
                    style={{ height: '50px' }}
                  >
                    <X size={22} color="var(--pmr-danger)" />
                  </Button>
                )}
              </InputGroup>
            </div>

          </Form.Group>

          {files.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>
                <span className="fw-normal">Selected Documents</span>
                <span style={{ color: "red", fontWeight: "bold" }}> ({files.length})</span> :
              </Form.Label>
              <div className="selected-documents-list">
                <ul>
                  {files.map((file, i) => (
                    <li key={i} className="d-flex align-items-center mb-1">
                      <span className="fw-bold">{i + 1}.</span>
                      <span className="fw-semibold">{getFileNameWithoutExtension(file.name)}</span>
                      <Button
                        variant="link"
                        className="text-danger p-0 remove-file-btn"
                        onClick={() => removeFile(i)}
                      >
                        <X size={16} color="var(--pmr-danger)" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="fw-normal">
              Report Type:
            </Form.Label>
            {loading ? (
              <StethoscopeLoading message="Loading..." />
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : !isAddingNew ? (
              <div className="w-100">
                <Form.Control
                  as="select"
                  name="groupName"
                  value={selectedGroup}
                  onChange={(e) => onGroupChange(e.target.value)}
                  className={`flatpickr-input-custom ${fieldErrors.groupName ? 'error-border' : ''}`}
                  style={fieldErrors.groupName ? inputErrorStyle : {}}
                >
                  <option value="">Select Report Type</option>
                  {groupsLst.map((group, index) => (
                    <option key={`group-${group.id || index}`} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </Form.Control>
                <div className="text-end">
                  <small
                    className="report-type-link"
                    onClick={toggleAddNew}
                  >
                    Report Type not found? Please click here to add new
                  </small>
                </div>
              </div>
            ) : (
              <div className="w-100">
                <Form.Control
                  type="text"
                  name="newGroupName"
                  value={formValues.newGroupName}
                  onChange={handleInputChange}
                  placeholder="Enter new report type"
                  className="flatpickr-input-custom"
                />
                <div className="text-end">
                  <small
                    className="save-link me-2 fw-bold"
                    onClick={addNewGroup}
                  >
                    Save
                  </small>
                  <small
                    className="cancel-link fw-bold"
                    onClick={cancelNewGroup}
                  >
                    Cancel
                  </small>
                </div>
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-normal">Provider Name:</Form.Label>
            <Form.Control
              type="text"
              name="providerName"
              value={formValues.providerName}
              onChange={handleInputChange}
              className={`flatpickr-input-custom ${fieldErrors.providerName ? 'error-border' : ''}`}
              style={{ height: '50px' }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-normal">Clinic / Diagnostics Center Name:</Form.Label>
            <Form.Control
              type="text"
              name="hospitalName"
              value={formValues.hospitalName}
              onChange={handleInputChange}
              className={`flatpickr-input-custom ${fieldErrors.hospitalName ? 'error-border' : ''}`}
              style={{ height: '50px' }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-normal">
              Report Date:
            </Form.Label>
            <div style={{ position: "relative" }}>
              <Flatpickr
                value={formValues.reportDate}
                onChange={([date]) => handleDateChange(date)}
                options={{
                  dateFormat: "m/d/Y",
                  maxDate: new Date(), // Disable future dates
                  disable: [
                    function (date) {
                      // Disable future dates
                      return date > new Date();
                    }
                  ]
                }}
                className={`form-control flatpickr-input-custom ${fieldErrors.reportDate ? 'error-border' : ''}`}
                placeholder="mm/dd/yyyy"
                style={fieldErrors.reportDate ? inputErrorStyle : {}}
              />
              <span
                className="flatpickr-calendar-icon"
                onClick={() => document.querySelector(".flatpickr-input").focus()}
              >
                <Calendar size={20} />
              </span>
            </div>
          </Form.Group>
        </Form>

        {submitted && (
          <div className="progress-wrapper mt-3">
            Report is being uploaded. Please wait...
            <ProgressBar
              variant="primary"
              now={progress}
              striped
              animated
            />
          </div>
        )}
      </Modal.Body>

      {!submitted && (
        <Modal.Footer className="justify-content-center">
          <Button
            variant="primary"
            onClick={onUpload}
            className="upload-btn me-2"
            disabled={uploadInProgressRef.current}
          >
            Upload
          </Button>
          <Button
            variant="danger"
            onClick={handleClose}
            className="upload-btn ms-2"
          >
            Close
          </Button>
        </Modal.Footer>
      )}

      {/* Custom Success Modal */}
      <CustomSuccessModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        message={successMessage}
        title="Success!"
      />

      <style>
        {`
          .error-border {
            border: 1px solid #dc3545 !important;
          }
          
          .drag-drop-area {
            transition: all 0.2s ease;
            border-radius: 4px;
            border: 2px dashed transparent;
            padding: 2px;
            position: relative;
          }
          
          .drag-drop-area.drag-over {
            background-color: #f8f9fa;
            border: 2px dashed #007bff !important;
          }
          
          .drag-drop-area .input-group {
            position: relative;
          }
          
          .drag-drop-area.drag-over::after {
            content: 'Drop files here to add to your selection';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(0, 123, 255, 0.95);
            color: white;
            padding: 15px 25px;
            border-radius: 6px;
            font-weight: bold;
            font-size: 14px;
            z-index: 1000;
            pointer-events: none;
            box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
          }
          
          .drag-drop-area:not(.drag-over)::before {
            content: 'Drag & drop files here or click "Choose Document(s)" - Multiple files will be added together';
            position: absolute;
            bottom: -25px;
            left: 0;
            right: 0;
            text-align: center;
            font-size: 12px;
            color: var(--pmr-danger);
            font-weight: bold;
          }
        `}
      </style>
    </Modal>
  );
};

export default UploadReports;